import MenuItem from 'apollo-react/components/MenuItem';
import Select from 'apollo-react/components/Select';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getSavedSearchColumns } from '../../../../actions/SavedSearchColumnsActions';
import { getSavedSearchesByEntity } from '../../../../actions/SavedSearchesActions';
import { ENTITY_NAME_DASHBOARD } from '../../../../constants/SavedSearches';
import { useTranslation } from '../../../../custom-hooks/useTranslation';
import { getSavedSearchResultsKey } from '../../../../reducers/SavedSearchResultsReducer';
import { isEmpty } from '../../../../utils/ramdaUtils';
import DashboardDialog from '../DashboardDialog';

const BarChartEdit = ({ part, tab, onAccept, onCancel }) => {
  const dispatch = useDispatch();
  const savedSearches = useSelector((state) => state.savedSearches);
  const savedSearchColumns = useSelector((state) => state.savedSearchColumns);
  const translateString = useTranslation();

  const { savedSearchByEntity: searchConfig, groupBy } = tab.graph;

  const [data, setData] = useState({ part, tab });
  const [entityName, setEntityName] = useState(searchConfig.entityName);
  const [savedSearchName, setSavedSearchName] = useState(searchConfig.savedSearchName);
  const [groupByField, setGroupByField] = useState(groupBy);
  const [disabled, setDisabled] = useState(false);

  const savedSearchGroups = savedSearches?.[ENTITY_NAME_DASHBOARD]?.groups ?? [];
  const columns = _.get(
    savedSearchColumns,
    [getSavedSearchResultsKey(entityName, savedSearchName), 'columns'],
    []
  );

  useEffect(() => {
    dispatch(getSavedSearchesByEntity(ENTITY_NAME_DASHBOARD));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEmpty(entityName) && !isEmpty(savedSearchName)) {
      dispatch(getSavedSearchColumns(entityName, savedSearchName));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, savedSearchName]);

  function handleDataChange(updates) {
    const clonedData = JSON.parse(JSON.stringify(data));

    updates.forEach(({ path, value }) => {
      _.set(clonedData, path, value);
    });

    setData(clonedData);
  }

  useEffect(() => {
    if (!isEmpty(groupByField)) {
      const column = columns.find((col) => col.accessor === groupByField);
      if (column) {
        handleDataChange([
          { path: 'tab.title', value: savedSearchName },
          { path: 'tab.subtitle', value: `${savedSearchName} by ${column.header}` },
          { path: 'tab.graph.groupBy', value: groupByField },
          {
            path: 'tab.graph.savedSearchByEntity',
            value: {
              entityName,
              savedSearchName,
            },
          },
        ]);
        setDisabled(isEmpty(entityName) || isEmpty(savedSearchName) || isEmpty(groupByField));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupByField]);

  useEffect(() => {
    if (isEmpty(entityName) || isEmpty(savedSearchName)) {
      setDisabled(true);
    }
  }, [entityName, savedSearchName]);

  const handleSearchChange = (e) => {
    switch (e.target.name) {
      case 'entityName':
        setEntityName(e.target.value);
        setSavedSearchName('');
        setGroupByField('');
        break;

      case 'savedSearchName':
        setSavedSearchName(e.target.value);
        setGroupByField('');
        break;

      case 'groupByField':
        setGroupByField(e.target.value);
        break;

      default:
    }
  };

  function getSearchGroups() {
    if (!isEmpty(savedSearchGroups)) {
      return savedSearchGroups.map((group) => (
        <MenuItem key={group.entityName} value={group.entityName}>
          {group.translatedEntityName}
        </MenuItem>
      ));
    }
    return !isEmpty(entityName) ? <MenuItem value={entityName}>{entityName}</MenuItem> : null;
  }

  function getSavedSearches() {
    if (!isEmpty(savedSearchGroups)) {
      const searchGroup = savedSearchGroups.find((group) => group.entityName === entityName) || {
        savedSearches: [],
      };
      return searchGroup.savedSearches.map((search) => (
        <MenuItem key={search.searchName} value={search.searchName}>
          {search.translatedSearchName}
        </MenuItem>
      ));
    }
    return !isEmpty(savedSearchName) ? (
      <MenuItem value={savedSearchName}>{savedSearchName}</MenuItem>
    ) : null;
  }

  function getSavedSearchFields() {
    const hasSelectedSearchGroup = !_.isNil(
      savedSearchGroups.find((group) => group.entityName === entityName)
    );
    if (hasSelectedSearchGroup && !isEmpty(columns)) {
      return columns.map((column) => (
        <MenuItem key={column.accessor} value={column.accessor}>
          {`${column.header} (${column.accessor})`}
        </MenuItem>
      ));
    }
    return hasSelectedSearchGroup && !isEmpty(groupByField) ? (
      <MenuItem value={groupByField}>{groupByField}</MenuItem>
    ) : null;
  }

  const cancelButton = {
    label: translateString('Cancel'),
    onClick: onCancel,
  };

  const acceptButton = {
    label: translateString('Accept'),
    onClick: () => onAccept(data),
    disabled,
  };

  return (
    <DashboardDialog
      data={data}
      buttonProps={[cancelButton, acceptButton]}
      onChange={handleDataChange}
      onClose={onCancel}
    >
      <div>
        <Select
          fullWidth
          required
          canDeselect={false}
          error={isEmpty(entityName)}
          helperText={isEmpty(entityName) ? translateString('This field is required') : ''}
          label={translateString('Search Group')}
          placeholder={translateString('Select Search Group')}
          name="entityName"
          value={entityName}
          onChange={handleSearchChange}
        >
          {getSearchGroups()}
        </Select>
      </div>
      <div>
        <Select
          fullWidth
          required
          canDeselect={false}
          error={isEmpty(savedSearchName)}
          helperText={isEmpty(savedSearchName) ? translateString('This field is required') : ''}
          label={translateString('Saved Search')}
          placeholder={translateString('Select Saved Search')}
          name="savedSearchName"
          value={savedSearchName}
          onChange={handleSearchChange}
        >
          {getSavedSearches()}
        </Select>
      </div>
      <div>
        <Select
          fullWidth
          required
          canDeselect={false}
          error={isEmpty(groupByField)}
          helperText={isEmpty(groupByField) ? translateString('This field is required') : ''}
          label={translateString('Aggregation Field')}
          placeholder={translateString('Select Field for Aggregation')}
          name="groupByField"
          value={groupByField}
          onChange={handleSearchChange}
        >
          {getSavedSearchFields()}
        </Select>
      </div>
    </DashboardDialog>
  );
};

export default BarChartEdit;
